import { TrendingUp, Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDate, formatLastOrderCatalogSummary } from "@/utils/formatters";
import { cn } from "@/lib/utils";
import type { CustomerProductPurchaseSummary } from "@/hooks/useCustomerProductHistory";

export interface CustomerProductHistoryWithClientProps {
  loading: boolean;
  history: CustomerProductPurchaseSummary | null | undefined;
  title?: string;
  density?: "default" | "compact" | "page";
  className?: string;
  emptyMessage?: string;
  onLastSaleClick?: () => void;
}

export function CustomerProductHistoryWithClient({
  loading,
  history,
  title = "Histórico com este Cliente",
  density = "default",
  className,
  emptyMessage,
  onLastSaleClick,
}: CustomerProductHistoryWithClientProps) {
  const isCompact = density === "compact";
  const isPage = density === "page";
  const firstTimeMessage =
    emptyMessage ??
    (isCompact
      ? "Primeira compra deste produto"
      : "Primeira compra deste produto por este cliente");

  return (
    <div
      className={cn(
        "rounded-lg border bg-muted/30",
        isCompact ? "p-3" : "p-4",
        className,
      )}
    >
      <div className={cn("flex items-center gap-2", isCompact ? "mb-2" : "mb-3")}>
        <TrendingUp className="h-4 w-4 shrink-0 text-primary" />
        <h3
          className={cn(
            "font-semibold text-sm",
            isPage && "text-base font-medium",
          )}
        >
          {title}
        </h3>
      </div>

      {loading ? (
        <p
          className={cn("text-muted-foreground", isCompact ? "text-xs" : "text-sm")}
        >
          {isCompact ? "Carregando..." : "Carregando histórico..."}
        </p>
      ) : history ? (
        <>
          <p
            className={cn(
              "font-semibold text-foreground",
              isCompact ? "text-sm" : "text-base",
            )}
          >
            {formatLastOrderCatalogSummary(
              history.last_quantity,
              history.last_unit_price,
            )}
          </p>

          {isPage ? (
            <>
              {history.last_sale_number && (
                <p
                  className={cn(
                    "mt-1 text-muted-foreground",
                    isCompact ? "text-xs" : "text-sm",
                  )}
                >
                  Pedido {history.last_sale_number}
                </p>
              )}
              <div className="mt-4 grid grid-cols-2 gap-3 border-t pt-3">
                <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3">
                  <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Última compra</p>
                    <p className="text-sm font-medium">
                      {history.last_sale_date ? formatDate(history.last_sale_date) : "—"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Dias sem comprar</p>
                    <p className="text-sm font-medium">
                      {history.days_since_last_purchase !== null
                        ? `${history.days_since_last_purchase} dias`
                        : "—"}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {history.last_sale_number ? (
                <div
                  className={cn(
                    "mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1",
                    isCompact ? "text-xs" : "text-sm",
                  )}
                >
                  {onLastSaleClick ? (
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 font-medium"
                      onClick={onLastSaleClick}
                    >
                      Pedido {history.last_sale_number}
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">
                      Pedido {history.last_sale_number}
                    </span>
                  )}
                  {history.last_sale_date ? (
                    <span className="text-muted-foreground">
                      · {formatDate(history.last_sale_date)}
                    </span>
                  ) : null}
                  {history.days_since_last_purchase !== null ? (
                    <span className="text-muted-foreground">
                      · há {history.days_since_last_purchase}{" "}
                      {history.days_since_last_purchase === 1 ? "dia" : "dias"}
                    </span>
                  ) : null}
                </div>
              ) : (
                (history.last_sale_date ||
                  history.days_since_last_purchase !== null) && (
                  <div
                    className={cn(
                      "mt-1.5 flex flex-wrap items-center gap-x-2 text-muted-foreground",
                      isCompact ? "text-xs" : "text-sm",
                    )}
                  >
                    {history.last_sale_date ? (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {formatDate(history.last_sale_date)}
                      </span>
                    ) : null}
                    {history.days_since_last_purchase !== null ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        há {history.days_since_last_purchase}{" "}
                        {history.days_since_last_purchase === 1 ? "dia" : "dias"}
                      </span>
                    ) : null}
                  </div>
                )
              )}
            </>
          )}
        </>
      ) : (
        <p
          className={cn("text-muted-foreground", isCompact ? "text-xs" : "text-sm")}
        >
          {firstTimeMessage}
        </p>
      )}
    </div>
  );
}
